// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/game_overlay/components/GameOverlayPlacementPreview.module.css';
import GameOverlaySettings from '@app/features/game_overlay/state/GameOverlaySettings';
import {
	anchorGameOverlayAt,
	computeGameOverlayPosition,
	computeGameOverlaySize,
} from '@app/features/game_overlay/utils/GameOverlayModel';
import type {DesktopGameOverlayDisplay} from '@app/types/electron.d';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useRef} from 'react';

const SAMPLE_ROWS = 4;
const KEYBOARD_STEP_PX = 16;

const GAME_OVERLAY_DRAG_HANDLE_DESCRIPTOR = msg({
	message: 'In-game overlay position. Use the arrow keys to move it.',
	comment: 'Accessible label of the draggable overlay block in the small screen preview in the overlay settings.',
});

const ARROW_KEY_STEPS: Record<string, {x: number; y: number}> = {
	ArrowLeft: {x: -KEYBOARD_STEP_PX, y: 0},
	ArrowRight: {x: KEYBOARD_STEP_PX, y: 0},
	ArrowUp: {x: 0, y: -KEYBOARD_STEP_PX},
	ArrowDown: {x: 0, y: KEYBOARD_STEP_PX},
};

interface GameOverlayPlacementPreviewProps {
	display: DesktopGameOverlayDisplay;
	scale: number;
	disabled: boolean;
}

export const GameOverlayPlacementPreview: React.FC<GameOverlayPlacementPreviewProps> = observer(
	({display, scale, disabled}) => {
		const {i18n} = useLingui();
		const {mode, corner, offsetX, offsetY} = GameOverlaySettings;
		const drag = useRef<{grabX: number; grabY: number; screen: DOMRect} | null>(null);
		const overlay = computeGameOverlaySize(mode, SAMPLE_ROWS, 0, scale);
		const position = computeGameOverlayPosition({corner, offsetX, offsetY}, overlay, display);

		const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>): void => {
			const screen = event.currentTarget.parentElement?.getBoundingClientRect();
			if (!screen) return;
			const block = event.currentTarget.getBoundingClientRect();
			event.currentTarget.setPointerCapture(event.pointerId);
			drag.current = {grabX: event.clientX - block.left, grabY: event.clientY - block.top, screen};
		};

		const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>): void => {
			if (!drag.current) return;
			const {grabX, grabY, screen} = drag.current;
			const ratio = display.width / screen.width;
			GameOverlaySettings.setAnchor(
				anchorGameOverlayAt(
					{x: (event.clientX - screen.left - grabX) * ratio, y: (event.clientY - screen.top - grabY) * ratio},
					overlay,
					display,
				),
			);
		};

		const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
			const step = ARROW_KEY_STEPS[event.key];
			if (!step) return;
			event.preventDefault();
			GameOverlaySettings.setAnchor(
				anchorGameOverlayAt({x: position.x + step.x, y: position.y + step.y}, overlay, display),
			);
		};

		const stopDrag = (): void => {
			drag.current = null;
		};

		return (
			<div
				className={styles.screen}
				style={{aspectRatio: `${display.width} / ${display.height}`}}
				data-flx="game-overlay.game-overlay-placement-preview.screen"
			>
				<button
					type="button"
					className={clsx(styles.block, mode === 'room' && styles.room)}
					style={{
						left: `${(position.x / display.width) * 100}%`,
						top: `${(position.y / display.height) * 100}%`,
						width: `${(overlay.width / display.width) * 100}%`,
						height: `${(overlay.height / display.height) * 100}%`,
					}}
					disabled={disabled}
					aria-label={i18n._(GAME_OVERLAY_DRAG_HANDLE_DESCRIPTOR)}
					onPointerDown={handlePointerDown}
					onPointerMove={handlePointerMove}
					onPointerUp={stopDrag}
					onPointerCancel={stopDrag}
					onKeyDown={handleKeyDown}
					data-flx="game-overlay.game-overlay-placement-preview.block"
				>
					{Array.from({length: SAMPLE_ROWS}, (_, index) => (
						<span key={index} className={styles.row} data-flx="game-overlay.game-overlay-placement-preview.row" />
					))}
				</button>
			</div>
		);
	},
);
